import React, { forwardRef, useCallback, useRef } from "react"
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteRenderOptionState
} from "@mui/material"
import Autocomplete, { AutocompleteProps } from "../Autocomplete"
import { IndentationDropdownOptions, BreadcrumbItem } from "./types"
import { useStyles } from "./styles"
import {
  IndentationDropdownItem,
  IndentationDropdownBreadcrumbs
} from "./components"

type Props = AutocompleteProps & {
  label?: string
  value?: string | string[]
  options?: (IndentationDropdownOptions & { disabled?: boolean })[]
  withCheckbox?: boolean
  withTooltip?: boolean
  hideIndentationMarkers?: boolean
  labelClassName?: string
  onChange?: (
    event: React.SyntheticEvent,
    value: string | string[],
    reason?: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<IndentationDropdownOptions>
  ) => void
  disabled?: boolean
  getMarker?: (option: IndentationDropdownOptions) => React.ReactNode
  getChipLabel?: (option: IndentationDropdownOptions) => React.ReactNode
  className?: string
  error?: boolean
  helperText?: string
  variant?: "filled" | "outlined"
  noWrap?: boolean
  transformLabel?: (value: string) => string
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: IndentationDropdownOptions,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode
  breadcrumbs?: BreadcrumbItem[]
  onBreadcrumbClick?: (level: number, id?: string, details?: any) => void
}

const IndentationDropdown = forwardRef<unknown, Props>(
  (
    {
      label,
      value,
      options = [],
      withCheckbox,
      withTooltip = false,
      hideIndentationMarkers = false,
      onChange,
      disabled,
      getMarker,
      getChipLabel,
      className,
      error,
      helperText,
      variant,
      multiple = true,
      noWrap = false,
      transformLabel,
      renderOption,
      breadcrumbs,
      onBreadcrumbClick,
      labelClassName,
      ...rest
    },
    ref
  ) => {
    const { classes, cx } = useStyles({ multiple })

    const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0
    const inputRef = useRef<HTMLInputElement>(null)

    const defaultRenderOption = useCallback(
      (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: IndentationDropdownOptions & { disabled?: boolean },
        state: AutocompleteRenderOptionState
      ) => {
        return (
          <IndentationDropdownItem
            {...props}
            key={option.value}
            option={option}
            state={state}
            withCheckbox={withCheckbox}
            withTooltip={withTooltip}
            noWrap={noWrap}
            hideIndentationMarkers={hideIndentationMarkers}
            getMarker={getMarker}
            disabled={option.disabled}
            labelClassName={labelClassName}
          />
        )
      },
      [
        withCheckbox,
        withTooltip,
        noWrap,
        hideIndentationMarkers,
        multiple,
        getMarker
      ]
    )

    return (
      <Autocomplete
        ref={ref as any}
        multiple={multiple}
        disableCloseOnSelect
        variant={variant}
        className={cx(classes.autocompleteContainer, className)}
        withCheckbox={withCheckbox}
        label={transformLabel?.(value as string) || label}
        value={value}
        disabled={disabled}
        options={options}
        onChange={onChange}
        renderOption={
          (renderOption ||
            defaultRenderOption) as AutocompleteProps["renderOption"]
        }
        getChipLabel={getChipLabel}
        error={error}
        helperText={helperText}
        breadcrumbs={
          hasBreadcrumbs && (
            <IndentationDropdownBreadcrumbs
              breadcrumbs={breadcrumbs}
              onBreadcrumbClick={onBreadcrumbClick}
              inputRef={inputRef}
            />
          )
        }
        {...rest}
        InputProps={{ ...rest.InputProps, inputRef }}
        classes={{
          ...rest.classes,
          groupLabel: classes.groupLabel,
          container: cx(
            rest.classes?.container,
            hasBreadcrumbs && classes.container
          )
        }}
        getOptionLabel={option => {
          if (typeof option === "string") return option
          return (option as IndentationDropdownOptions).label ?? ""
        }}
      />
    )
  }
)

export default IndentationDropdown
